import { UserGroup } from './userGroup';
import { UserProfile } from './companyExternalUser';

export interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	preferredName: string;
	preferredPronouns: string;
	personalEmail: string;
	role: string;
	permissions: string;
	status: string;
	alwaysAddToProjects: boolean;
	created: string;
	updated: string;
	acceptedInvite: boolean;
	acceptedTermsAndConditions: boolean;
	acceptedTermsAndConditionsDate: string;
	googleId: string;
	googleProfilePictureUrl?: string;
	userProfile?: UserProfile;
	preferences: UserPreferences;
	helpers: UserHelpers;
	groups?: UserGroup[];
}

export interface UserDTO {
	email: string;
	firstName: string;
	lastName: string;
	googleId: string;
	role?: string;
	permissions?: string;
	groupIds?: string[];
}

export interface ConvertUserDTO {
	companyExternalUserId: string;
	googleId: string;
	googleEmail: string;
}

export interface UserPreferences {
	theme?: 'light' | 'dark' | 'orbito' | 'system';
}

export interface UserHelpers {
	tourTooltipViewed: boolean;
}

export interface UserUpdateDTO {
	id: string;
	firstName?: string;
	lastName?: string;
	role?: string;
	permissions?: string;
	alwaysAddToProjects?: boolean;
	status?: string;
	groupIds?: string[];
}

export interface Address {
	line1?: string;
	line2?: string;
	city?: string;
	state?: string;
	zip?: string;
	sameAsHomeAddress?: boolean;
}

export interface UpdateSelfDTO {
	firstName?: string;
	lastName?: string;
	preferences?: UserPreferences;
	helpers?: UserHelpers;
	acceptedTermsAndConditions?: boolean;
	acceptedTermsAndConditionsDate?: Date;

	// Onboarding fields
	preferredName?: string;
	preferredPronouns?: string;
	personalEmail?: string;
	phone?: string;
	phoneCarrier?: string;
	isIphone?: boolean;
	smsTextReady?: boolean;
	hasWhatsapp?: boolean;
	whatsappNumber?: string;
	hasWechat?: boolean;
	wechatNumber?: string;
	emergencyContactFirstName?: string;
	emergencyContactLastName?: string;
	emergencyContactPhone?: string;
	homeAddress?: Address;
	shippingAddress?: Address;
	role?: string;
	websiteUrl?: string;
	instagramHandle?: string;
	healthInsurance?: boolean;
	professionalLicenses?: string;
	dietaryRestrictions?: string;
	photographyConsent?: boolean;
	shirtSize?: string;
	pantSize?: string;
	shoeSize?: string;
	biopoc?: boolean;
	homeAirport?: string;
	preferredSeating?: string;
	dateOfBirth?: string;
	tsaPrecheck?: string;
	globalEntry?: string;
	passportNumber?: string;
	passportIssuingCountry?: string;
	passportExpirationDate?: string;
	noPassport?: boolean;
	preferredAirlines?: string[];
	deltaMileage?: string;
	unitedMileage?: string;
	driversLicenseNumber?: string;
	driversLicenseState?: string;
	noDriversLicense?: boolean;
	hasVehicle?: boolean;
	willingToUseVehicleForProduction?: boolean;
	vehicleType?: string;
	vehicleLocation?: string;
	status?: string;
}

export interface GoogleUser {
	name: {
		givenName: string;
		familyName: string;
	};
	primaryEmail: string;
	id: string;
	creationTime: string;
}

export interface DatedUserReference {
	id: string;
	name: string;
	date: string;
}

export interface UsersFilter {
	searchText?: string;
	order?: string;
	direction?: string;
	page?: number;
	take?: number;
	where?: any;
}

export const UserPermissions = {
	ADMIN: {
		id: 'ADMIN',
		display: 'Admin',
	},
	CREW: {
		id: 'CREW',
		display: 'Crew',
	},
};

export const UserPermissionsDDOptions = Object.values(UserPermissions);

// enum CrewRole {
// 	EXECUTIVE_PRODUCER = 'EXECUTIVE_PRODUCER',
// 	DIRECTOR = 'DIRECTOR',
// 	PRODUCER = 'PRODUCER',
// 	DEVELOPMENT_LEAD = 'DEVELOPMENT_LEAD',
// 	HEAD_OF_POST_PRODUCTION = 'HEAD_OF_POST_PRODUCTION',
// 	CAM_OP = 'CAM_OP',
// 	EDITOR = 'EDITOR',
// 	PRODUCTION_MANAGER = 'PRODUCTION_MANAGER',
// 	ACCOUNTING_FINANCE = 'ACCOUNTING_FINANCE',
// }

export const CrewRoles = {
	ADMIN: {
		id: 'ADMIN',
		display: 'Admin',
	},
	EXECUTIVE_PRODUCER: {
		id: 'EXECUTIVE_PRODUCER',
		display: 'Executive Producer',
	},
	DIRECTOR: {
		id: 'DIRECTOR',
		display: 'Director',
	},
	PRODUCER: {
		id: 'PRODUCER',
		display: 'Producer',
	},
	DEVELOPMENT_LEAD: {
		id: 'DEVELOPMENT_LEAD',
		display: 'Development Lead',
	},
	HEAD_OF_POST_PRODUCTION: {
		id: 'HEAD_OF_POST_PRODUCTION',
		display: 'Head of Post Production',
	},
	CAM_OP: {
		id: 'CAM_OP',
		display: 'Cam Op',
	},
	EDITOR: {
		id: 'EDITOR',
		display: 'Editor',
	},
	PRODUCTION_MANAGER: {
		id: 'PRODUCTION_MANAGER',
		display: 'Production Manager',
	},
	ACCOUNTING_FINANCE: {
		id: 'ACCOUNTING_FINANCE',
		display: 'Accounting Finance',
	},
	CINEMATOGRAPHER: {
		id: 'CINEMATOGRAPHER',
		display: 'Cinematographer',
	},
	SOUND_ENGINEER: {
		id: 'SOUND_ENGINEER',
		display: 'Sound Engineer',
	},
	SCRIPT_SUPERVISOR: {
		id: 'SCRIPT_SUPERVISOR',
		display: 'Script Supervisor',
	},
	PRODUCTION_ASSISTANT: {
		id: 'PRODUCTION_ASSISTANT',
		display: 'Production Assistant',
	},
	GAFFER: {
		id: 'GAFFER',
		display: 'Gaffer',
	},
};

export const CrewRolesDDOptions = Object.values(CrewRoles).sort((a, b) =>
	a.display.localeCompare(b.display)
);

export const UserStatus = {
	SUBSCRIBED: {
		id: 'SUBSCRIBED',
		display: 'Subscribed',
	},
	UNSUBSCRIBED: {
		id: 'UNSUBSCRIBED',
		display: 'Unsubscribed',
	},
};

export const UserStatusDDOptions = Object.values(UserStatus);
